import CryptoJS from 'crypto-js';
import { SECRET_KEY } from '@/constants';

/**
 * @description: 首字母大写
 * @param {string} str
 * @return {*}
*/
export const getFirstChar = (str?: string) => {
  if (!str) return 'U';
  const firstChar = str.charAt(0);
  if (/[a-zA-Z]/.test(firstChar)) {
    return firstChar.toUpperCase();
  } else {
    return firstChar;
  }
};

/**
 * @description: 比较两个JSON对象是否相等
 * @return {*}
*/
export const areJSONEqual = (json1: any, json2: any) => {
  if (typeof json1!== 'object' || typeof json2!== 'object') {
    return json1 === json2;
  }

  if (Array.isArray(json1) && Array.isArray(json2)) {
    if (json1.length!== json2.length) {
      return false;
    }
    for (let i = 0; i < json1.length; i++) {
      if (!areJSONEqual(json1[i], json2[i])) {
        return false;
      }
    }
    return true;
  }

  const keys1 = Object.keys(json1).sort();
  const keys2 = Object.keys(json2).sort();

  if (keys1.length!== keys2.length) {
    return false;
  }

  for (let i = 0; i < keys1.length; i++) {
    const key = keys1[i];
    if (!areJSONEqual(json1[key], json2[key])) {
      return false;
    }
  }

  return true;
}

/**
 * @description: 扁平化树形结构
*/
export const flattenTree = (tree: any) => {
  return tree.reduce((acc: any, item: any) => {
    const { children, ...rest } = item;
    return acc.concat(rest, children ? flattenTree(children) : []);
  }, []);
}

/**
 * 在树中递归查找 key 等于指定 value 的元素
 * @param {Object} tree - 树的根节点
 * @param {*} key - 要查找的值
 * @returns {Object|null} - 找到的节点或 null
 */
export const findNodeByKey = (tree: any[], key: string | number): any => {
  // 遍历整个树的数组
  for (let node of tree) {
    // 如果当前节点的 value 等于 key，则直接返回该节点
    if (node.value === key) {
      return node;
    }
    // 如果当前节点有 children，则递归查找子节点
    if (node.children && node.children.length > 0) {
      const result = findNodeByKey(node.children, key);
      if (result) {
        return result; // 如果在子节点中找到了，直接返回结果
      }
    }
  }
  // 如果没有找到，返回 null
  return null;
}

export const findParentsByKey = (tree: any[], key: string | number, parents: any[] = []): any[] | null => {
  for (let node of tree) {
    // 如果当前节点的 value 等于 key，则返回父元素数组
    if (node.value === key) {
      return parents;
    }
    // 如果当前节点有 children，则递归查找子节点
    if (node.children && node.children.length > 0) {
      // 在递归调用中，将当前节点加入父元素数组
      const result: any[] | null = findParentsByKey(node.children, key, [...parents, node]);
      if (result) {
        return result; // 如果找到结果，直接返回
      }
    }
  }
  // 如果没有找到，返回 null
  return null;
}

/**
 * @description: 通过key获取tree中某个元素的value为key父节点
 * @param {any[]} tree
 * @param {any} key
 * @return {any | null}
*/
export const findParentByKey = (tree: any[], key: string | number, parent: any = null): any | null => {
  for (let node of tree) {
    // 如果当前节点的 value 等于 key，返回当前的父节点
    if (node.value === key) {
      return parent;
    }
    // 如果当前节点有 children，则递归查找子节点
    if (node.children && node.children.length > 0) {
      const result = findParentByKey(node.children, key, node);
      if (result) {
        return result; // 如果找到结果，直接返回
      }
    }
  }
  // 如果没有找到，返回 null
  return null;
}

/**
 * 去除富文本中的 HTML 标签
 * @param {string} richText - 包含富文本内容的字符串
 * @return {string} - 去除 HTML 标签后的纯文本
 */
export function stripHtmlTags(richText: string) {
  if (!richText) return ''; // 如果传入的内容是空或者 null，直接返回空字符串
  return richText.replace(/<[^>]+>/g, '').trim(); // 使用正则表达式替换 HTML 标签
}

// 加密
export const encryptJson = (obj: any) => {
  const jsonStr = JSON.stringify(obj);
  return CryptoJS.AES.encrypt(jsonStr, SECRET_KEY).toString();
};

// 解密
export const decryptJson = (encrypted: string) => {
  const bytes = CryptoJS.AES.decrypt(encrypted, SECRET_KEY);
  const decrypted = bytes.toString(CryptoJS.enc.Utf8);
  return JSON.parse(decrypted);
};


/**
 * 把请求回来的递归数据转换成需要的key/value
 * @param data 数据
 * @param deep 希望返回的层级数，从1开始算，1就是返回一层
 * @returns
 */
export const transformTreeData = ({
  data = [] as any[],
  deep = 999,
  level = 0,
  newData = (item) => item,
}) => {
  let _level = level;
  if (Array.isArray(data) && data.length > 0) {
    _level++;
    data.forEach((item, index) => {
      const children = item.children;

      if (_level < deep) {
        transformTreeData({ data: children, deep, level: _level, newData });
      }

      const newItem = {
        level: _level,
        ...item,
        ...newData(item),
      };
      if (children && _level < deep) {
        newItem.children = children;
      } else {
        delete newItem.children;
      }

      data[index] = newItem;
    });
  }
  _level--;
  return data;
};



export const normFile = (e: any) => {
  if (Array.isArray(e)) {
    return e;
  }
  return e?.fileList;
};


export const loadScript = ({ id, url }) => {
  return new Promise((resolve) => {
    const el = document.getElementById(id);
    if (el) return;
    const script = document.createElement('script');
    script.setAttribute('id', id);
    script.setAttribute('src', url);
    script.onload = () => {
      resolve(true);
    };
    document.head.appendChild(script)
  });
};